import { Row, Table } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useAccountsTableColumns } from "./hooks/accountsTableColumns";
import { TablePagination } from "../../components/TablePagination";
import { useFetchData } from "./hooks/fetchData";
import { useFetchDataCount } from "./hooks/fetchDataCount";
import { Filters } from "./components/Filters";
import { useIsOther } from "./hooks/isOther";
import { NavigateFunction, useNavigate } from "react-router-dom";
import ROUTES from "../../configs/routesConfig";
import { useGetRole } from "../../lib/hooks/getRole";
import "./styles.scss";
import { PortalNotification } from "./components/PortalNotification";
import dayjs from "../../configs/dayjs";
import { hideNotification, isNotificationHidden } from "./utils/notificationsStorage";

const COUPONS_NOTIFICATION_ID = "coupons";
const MAILING_NOTIFICATION_ID = "mailing";

type AccountNotification = {
  id: string;
  isVisible: boolean;
  title: string;
  description: string;
  actionLabel: string;
};

export const Accounts = () => {
  const navigate: NavigateFunction = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin] = useGetRole();

  const { isOther } = useIsOther();

  const { total, isLoading: isTotalLoading, request, fetchDataCount, setSearchParams } = useFetchDataCount(isOther);

  const { data, isLoading, fetchData } = useFetchData(request);

  const reloadData = useCallback((): void => {
    fetchData();
    fetchDataCount();
  }, [fetchData, fetchDataCount]);

  const navigateToAccount = useCallback(
    (id: string) => {
      navigate(`${ROUTES.ACCOUNTS}/${id}`);
    },
    [navigate]
  );

  const onRow = useCallback(
    (record: any) => ({
      onClick: () => {
        const id = isAdmin ? record.id : record.accountNumber;

        navigate(`${ROUTES.ACCOUNTS}/${id}`);
      }
    }),
    [isAdmin, navigate]
  );

  const { columns, onSortChange } = useAccountsTableColumns(reloadData, navigateToAccount);

  const onPageChange = (pageNumber: number, pageSize: number) => {
    const newRequest = { ...request, pageNumber, pageSize };

    setSearchParams(newRequest);
  };

  const onSorterChange = (_pagination: any, _filter: any, sorter: any) => {
    onSortChange(sorter, request);
  };

  const notifications = useMemo<AccountNotification[]>(
    () => [
      {
        id: COUPONS_NOTIFICATION_ID,
        isVisible: !isAdmin && dayjs().year() < 2027,
        title: "Starting in 2027, we will no longer send out payment coupons or 1098s.",
        description: "You can print them here at any time. If you are unable to print, contact our office.",
        actionLabel: "I Understand"
      },
      {
        id: MAILING_NOTIFICATION_ID,
        isVisible: !isAdmin && dayjs().year() < 2027,
        title: "Starting in 2027, payment receipts and deposit receipts will no longer be sent by mail.",
        description: "Please contact our office if you still need a printed copy.",
        actionLabel: "I Understand"
      }
    ],
    [isAdmin]
  );

  const [dismissedNotifications, setDismissedNotifications] = useState<Set<string>>(() => new Set());

  const visibleNotifications = notifications.filter(
    ({ id, isVisible }) => isVisible && !dismissedNotifications.has(id) && !isNotificationHidden(id)
  );

  const onCloseNotification = useCallback((id: string) => {
    hideNotification(id);
    setDismissedNotifications((dismissed) => new Set(dismissed).add(id));
  }, []);

  const containerClassName: string = useMemo(
    () =>
      `accounts table-page-content ${isAdmin ? "admin" : ""} ${visibleNotifications.length ? "portal-with-not" : ""}`,
    [isAdmin, visibleNotifications.length]
  );

  return (
    <Row className={containerClassName}>
      {visibleNotifications.map(({ id, title, description, actionLabel }) => (
        <PortalNotification
          key={id}
          title={title}
          description={description}
          actionLabel={actionLabel}
          onClose={() => onCloseNotification(id)}
        />
      ))}
      <Filters request={request} isLoading={isLoading} isOther={isOther} />
      <Row className="d-block table__container w-100">
        <Table
          dataSource={data}
          columns={columns}
          loading={isLoading}
          pagination={false}
          className="overflow-y-auto clickable-rows-table"
          rowKey="id"
          onChange={onSorterChange}
          showSorterTooltip={false}
          onRow={onRow}
        />
        <TablePagination
          isTotalLoading={isTotalLoading}
          total={total}
          currentPage={request.pageNumber}
          pageSize={request.pageSize}
          onPageChange={onPageChange}
        />
      </Row>
    </Row>
  );
};
